import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { RootState } from '../../../types/common.types';
import { SupportTemplate } from '../../../types/entity.types';

import { useProgrammeApi } from '../../../api/programme/useProgrammeApi';
import { useSupportTemplateApi } from '../../../api/support-template/useSupportTemplateApi';
import { useSupportApi } from '../../../api/support/useSupportApi';

import { UPCOMING } from '../../../utils/constants';

import Support from '../../../components/screens/programme/support/Support';

const SupportContainer = () => {
  const { supportTemplate, messageOverviews } = useSelector(({ support }: RootState) => support);

  const navigate = useNavigate();

  const { status, programmeExternalReference } = useParams();

  const [searchParams] = useSearchParams();

  const hasSubProgrammes = searchParams.get('hasSubProgrammes') === 'true';

  const { viewProgramme } = useProgrammeApi();

  const {
    createSupportTemplate: createSupportTemplateRequest,
    viewSupportTemplate,
    updateSupportTemplate: updateSupportTemplateRequest
  } = useSupportTemplateApi();

  const { viewMessageOverview, deleteMessage: deleteMessageRequest } = useSupportApi();

  useEffect(() => {
    if (!programmeExternalReference) return;

    viewProgramme({ externalReference: programmeExternalReference });
    viewSupportTemplate({ programmeExternalReference });

    let interval: NodeJS.Timeout;
    if (status !== UPCOMING) {
      viewMessageOverview({ programmeExternalReference, showSpinner: true });
      interval = setInterval(() => {
        viewMessageOverview({ programmeExternalReference });
      }, 5000);
    }

    return () => clearInterval(interval);
  }, [programmeExternalReference, status, viewProgramme, viewSupportTemplate, viewMessageOverview]);

  const openMessages = (chatExternalReference: string) => {
    navigate(
      `/programmes/${status}/${programmeExternalReference}/support/${chatExternalReference}?hasSubProgrammes=${hasSubProgrammes}`
    );
  };

  const navigateToNewChat = () => {
    navigate(`/programmes/${status}/${programmeExternalReference}/support/new?hasSubProgrammes=${hasSubProgrammes}`);
  };

  const createSupportTemplate = (supportTemplate: SupportTemplate, redirectUrl?: string) => {
    createSupportTemplateRequest(
      {
        programmeExternalReference: programmeExternalReference!,
        supportTemplate
      },
      () => {
        if (!redirectUrl) return;

        navigate(redirectUrl);
      }
    );
  };

  const updateSupportTemplate = (supportTemplate: SupportTemplate, redirectUrl?: string) => {
    updateSupportTemplateRequest(
      {
        programmeExternalReference: programmeExternalReference!,
        supportTemplate
      },
      () => {
        if (!redirectUrl) return;

        navigate(redirectUrl);
      }
    );
  };

  const deleteMessage = (systemUserExternalReference: string) => {
    deleteMessageRequest({
      programmeExternalReference: programmeExternalReference!,
      systemUserExternalReference
    });
  };

  return (
    <Support
      status={status!}
      messageOverviews={messageOverviews}
      supportTemplate={supportTemplate}
      openMessages={openMessages}
      navigateToNewChat={navigateToNewChat}
      createSupportTemplate={createSupportTemplate}
      updateSupportTemplate={updateSupportTemplate}
      deleteMessage={deleteMessage}
    />
  );
};

export default SupportContainer;
